import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2, ArrowRight, ArrowUpRight, ArrowDown, Plus, Minus, Phone, Wand2,
  Layers, Weight, Boxes, Check, Star, ShoppingCart, Repeat, FileText, Presentation,
  Scale, X, Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { COMPANY, PRODUCTS, IMAGES, CATEGORIES } from "../lib/data";
import { Reveal } from "../components/site/Reveal";
import { CTABand, FaqSection } from "../components/site/Bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { trackEvent, getVisitorId, getAttribution } from "../lib/track";

const FUNCTION_LABEL = {
  access: "Access / Roadway",
  "crane-pad": "Crane / Outrigger",
  turf: "Turf / Ground Cover",
  rig: "Rig Platform",
  trench: "Trench / Excavation",
};

const AXES = [
  { key: "material", label: "By Material", icon: Layers, values: ["Timber", "Composite", "Steel / Aluminum", "Rubber"] },
  { key: "function", label: "By Function", icon: Boxes, values: ["access", "crane-pad", "turf", "rig", "trench"] },
  { key: "load", label: "By Load Rating", icon: Weight, values: ["Light", "Medium", "Heavy"] },
];

const LOAD_STYLE = {
  Light: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Heavy: "bg-flame/15 text-flame border-flame/40",
};

const PATHS = [
  {
    key: "buy", icon: ShoppingCart, title: "Purchase",
    tag: "Own the mats · deploy & reuse",
    desc: "Buy whatever mat makes the most sense for your project — any type, any manufacturer, with the flagship Big Bad Mats when durability matters most. Own a reusable fleet you control, at the lowest lifecycle cost.",
    points: ["Best for recurring, in-house use", "Lowest long-term cost per use", "Full control of your fleet & schedule"],
  },
  {
    key: "rent", icon: Repeat, title: "Rent",
    tag: "We deliver, install & remove",
    desc: "Rent the right mat for your project — any type, any manufacturer, flagship Big Bad Mats — and let us handle everything: delivery, installation, maintenance, and removal. No capital purchase, no logistics burden.",
    points: ["Best for one-off or short-term jobs", "No capital outlay or storage", "Turnkey — we handle the logistics"],
  },
];

// "Which is right for me?" quick-contrast rows (Purchase vs Rent)
const DECIDER_ROWS = [
  { factor: "Use frequency", buy: "Recurring / ongoing jobs", rent: "One-off or occasional jobs" },
  { factor: "Project length", buy: "Long-term or permanent", rent: "Short-to-medium term" },
  { factor: "Upfront cost", buy: "Capital purchase (lowest cost per use over time)", rent: "No capital outlay — pay per project" },
  { factor: "Logistics", buy: "You store, transport & maintain", rent: "We deliver, install, maintain & remove" },
  { factor: "Best when", buy: "You want to own a reusable fleet you control", rent: "You want zero storage & logistics burden" },
];

// Flagship comparison record — Big Bad Mats is auto-added to the chart when relevant.
const BBM_COMPARE = {
  key: "big-bad-mats",
  name: "Big Bad Mats",
  material: "Composite",
  load_rating: "Heavy",
  functions: ["access", "crane-pad", "rig", "turf"],
  dimensions: "Interlocking composite panels · multiple sizes",
  weight_capacity: "Heavy equipment & sustained loads — engineered for the highest durability and reuse per mat",
  applications: ["Temporary access roads", "Crane & rig work platforms", "Utility right-of-way", "Turf & soil protection"],
  image: IMAGES.bigbadmats,
  flagship: true,
};

// Big Bad Mats is "relevant" when any selected mat shares one of its functions.
const bbmRelevant = (selectedMats) =>
  selectedMats.some((m) => (m.functions || []).some((f) => BBM_COMPARE.functions.includes(f)));

const MAX_COMPARE = 4; // user-selectable mats (Big Bad Mats added on top when relevant)

import { Seo } from "../components/site/Seo";

export default function GroundProtection() {
  const navigate = useNavigate();
  const [mats, setMats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState(null); // "buy" | "rent"
  const [axis, setAxis] = useState("material");
  const [value, setValue] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [compareKeys, setCompareKeys] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const step1Ref = useRef(null);
  const productsRef = useRef(null);

  useEffect(() => {
    api.get("/mats").then(({ data }) => setMats(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const avenue = path || "buy";

  const choosePath = (key) => {
    setPath(key);
    trackEvent("cta_click", { label: `Ground Protection path: ${key === "buy" ? "Purchase" : "Rent"}`, product: "Ground Protection" });
    setTimeout(() => productsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const activeAxis = AXES.find((a) => a.key === axis);
  const counts = useMemo(() => {
    const c = { all: mats.length };
    activeAxis.values.forEach((v) => {
      c[v] = mats.filter((m) =>
        axis === "material" ? m.material === v :
        axis === "function" ? (m.functions || []).includes(v) :
        m.load_rating === v
      ).length;
    });
    return c;
  }, [mats, axis, activeAxis]);

  const visible = useMemo(() => {
    if (value === "all") return mats;
    return mats.filter((m) =>
      axis === "material" ? m.material === value :
      axis === "function" ? (m.functions || []).includes(value) :
      m.load_rating === value
    );
  }, [mats, axis, value]);

  const changeAxis = (k) => { setAxis(k); setValue("all"); };

  // --- Mat comparison ---
  const isComparing = (key) => compareKeys.includes(key);
  const toggleCompare = (key) => {
    setCompareKeys((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      const userCount = prev.filter((k) => k !== "big-bad-mats").length;
      if (key !== "big-bad-mats" && userCount >= MAX_COMPARE) return prev; // cap user picks
      return [...prev, key];
    });
  };
  const clearCompare = () => setCompareKeys([]);

  const compareColumns = useMemo(() => {
    const picked = compareKeys
      .map((k) => (k === "big-bad-mats" ? BBM_COMPARE : mats.find((m) => m.key === k)))
      .filter(Boolean);
    const userMats = picked.filter((m) => m.key !== "big-bad-mats");
    const userPickedBBM = compareKeys.includes("big-bad-mats");
    const includeBBM = userPickedBBM || bbmRelevant(userMats);
    const cols = [];
    if (includeBBM) cols.push(BBM_COMPARE);
    userMats.forEach((m) => cols.push(m));
    return { cols, autoBBM: includeBBM && !userPickedBBM };
  }, [compareKeys, mats]);

  const canCompare = compareColumns.cols.length >= 2;
  const openCompare = () => {
    if (!canCompare) return;
    trackEvent("cta_click", { label: `Mat comparison opened (${compareColumns.cols.length} mats)`, product: "Ground Protection" });
    setCompareOpen(true);
  };

  const gpCategory = CATEGORIES.find((c) => c.slug === "ground-protection");
  const gpFaqs = gpCategory?.faqs || [];
  const gpJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://oneharvestco.com/" },
        { "@type": "ListItem", position: 2, name: "Ground Protection & Site Access", item: "https://oneharvestco.com/ground-protection" },
      ],
    },
    ...(gpFaqs.length
      ? [{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: gpFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }]
      : []),
  ];

  return (
    <div data-testid="ground-protection-page" className="pt-[72px]">
      <Seo title="Ground Protection Mats" description="Rent or purchase heavy-duty ground protection matting for site access, crane pads, and right-of-way work. Any mat you need, from a single-source partner." jsonLd={gpJsonLd} />
      {/* HERO */}
      <section className="bg-ink relative overflow-hidden" data-testid="gp-hero">
        <div className="absolute inset-0 grain opacity-10" />
        <img src={IMAGES.bigbadmats} alt="Ground protection matting" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/60" />
        <div className="container-x py-20 md:py-28 relative">
          <div className="kicker text-flame mb-4">Ground Protection &amp; Site Access · One Source</div>
          <h1 className="font-display font-black text-white text-4xl md:text-6xl tracking-tight max-w-4xl text-balance">
            Any mat for any jobsite — your way, start to finish.
          </h1>
          <p className="mt-5 text-white/70 text-lg max-w-2xl leading-relaxed">
            We're your single source for every ground-protection and site-access mat. Start by choosing how you want it —
            purchase or rent — then pick the right mat with our flagship Big Bad Mats and a guided selector to back you up.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="gp-hero-start"
              onClick={() => step1Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="inline-flex items-center gap-2 bg-flame text-white px-6 py-3.5 font-semibold hover:bg-flame/90 transition-colors">
              Start here — Purchase or Rent <ArrowDown className="h-4 w-4" />
            </button>
            <a href={`tel:${COMPANY.phone}`} data-testid="gp-hero-call"
              className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-3.5 font-semibold hover:border-flame hover:text-flame transition-colors">
              <Phone className="h-4 w-4" /> Contact Us
            </a>
          </div>
          {/* journey rail */}
          <div className="mt-12 flex flex-wrap items-center gap-3 text-sm" data-testid="gp-journey-rail">
            {[
              { n: 1, label: "Purchase or Rent" },
              { n: 2, label: "Choose your mat" },
              { n: 3, label: "Get a tailored quote" },
            ].map((s, i) => (
              <div key={s.n} className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 text-white/75">
                  <span className="h-6 w-6 grid place-items-center bg-flame/15 border border-flame/40 text-flame text-xs font-bold">{s.n}</span>
                  {s.label}
                </span>
                {i < 2 && <ArrowRight className="h-4 w-4 text-white/25" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STEP 1 — PURCHASE OR RENT */}
      <section ref={step1Ref} className="bg-background scroll-mt-[72px]" data-testid="gp-step1">
        <div className="container-x py-20 md:py-24">
          <Reveal>
            <div className="max-w-2xl">
              <div className="kicker text-flame mb-3">Step 1 · How would you like it?</div>
              <h2 className="font-display font-black text-ink text-3xl md:text-4xl tracking-tight">
                Purchase to own, or rent turnkey.
              </h2>
              <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
                Same single-source access to every mat — you just choose how to get it. Your choice tailors the rest of the
                journey and pre-fills your quote.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {PATHS.map((p) => {
              const active = path === p.key;
              return (
                <Reveal key={p.key}>
                  <button data-testid={`gp-path-${p.key}`} onClick={() => choosePath(p.key)}
                    className={`group text-left w-full h-full border p-8 flex flex-col transition-[transform,box-shadow,border-color,background-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1.5 hover:shadow-2xl ${
                      active ? "border-flame bg-flame/[0.06] ring-1 ring-flame" : "border-border bg-card hover:border-flame/60"
                    }`}>
                    <div className="flex items-center gap-4">
                      <div className={`h-14 w-14 flex items-center justify-center border transition-colors ${active ? "bg-flame text-white border-flame" : "bg-flame/10 border-flame/25 text-flame group-hover:bg-flame group-hover:text-white"}`}>
                        <p.icon className="h-7 w-7" strokeWidth={2} />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-flame">{p.tag}</div>
                        <h3 className="font-display font-black text-ink text-2xl md:text-3xl">{p.title}</h3>
                      </div>
                      {active && <span className="ml-auto inline-flex items-center gap-1.5 text-flame text-xs font-bold uppercase tracking-widest"><Check className="h-4 w-4" /> Selected</span>}
                    </div>
                    <p className="mt-5 text-charcoal leading-relaxed">{p.desc}</p>
                    <ul className="mt-5 space-y-2.5 flex-1">
                      {p.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 text-flame shrink-0 mt-0.5" /> {pt}
                        </li>
                      ))}
                    </ul>
                    <span className={`mt-7 inline-flex items-center gap-2 font-semibold ${active ? "text-flame" : "text-ink group-hover:text-flame"} transition-colors`}>
                      {active ? "Continue to mat selection" : `Choose ${p.title}`} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Reveal>
              );
            })}
          </div>

          {/* Which is right for me? — quick contrast strip */}
          <Reveal>
            <div className="mt-12 border border-border bg-card" data-testid="gp-decider-strip">
              <div className="flex items-center gap-3 border-b border-border px-6 py-4">
                <Scale className="h-5 w-5 text-flame" />
                <h3 className="font-display font-bold text-lg text-ink">Which is right for me?</h3>
                <span className="text-sm text-muted-foreground hidden sm:inline">A quick side-by-side to help you decide.</span>
              </div>
              {/* header */}
              <div className="grid grid-cols-3 bg-ink text-white text-[11px] uppercase tracking-widest">
                <div className="p-4 border-r border-white/10">Factor</div>
                <div className="p-4 border-r border-white/10 inline-flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-flame" /> Purchase</div>
                <div className="p-4 inline-flex items-center gap-2"><Repeat className="h-4 w-4 text-flame" /> Rent</div>
              </div>
              {DECIDER_ROWS.map((row, i) => (
                <div key={row.factor} className={`grid grid-cols-3 border-t border-border ${i % 2 ? "bg-muted/40" : "bg-card"}`}>
                  <div className="p-4 border-r border-border font-semibold text-ink text-sm">{row.factor}</div>
                  <div className="p-4 border-r border-border text-sm text-charcoal leading-relaxed">{row.buy}</div>
                  <div className="p-4 text-sm text-charcoal leading-relaxed">{row.rent}</div>
                </div>
              ))}
              <div className="flex flex-col sm:flex-row gap-3 px-6 py-5 border-t border-border">
                <button data-testid="gp-decider-buy" onClick={() => choosePath("buy")}
                  className="inline-flex items-center justify-center gap-2 bg-ink text-white px-6 py-3 font-semibold hover:bg-flame transition-colors">
                  <ShoppingCart className="h-4 w-4" /> I'll purchase
                </button>
                <button data-testid="gp-decider-rent" onClick={() => choosePath("rent")}
                  className="inline-flex items-center justify-center gap-2 border border-ink text-ink px-6 py-3 font-semibold hover:bg-ink hover:text-white transition-colors">
                  <Repeat className="h-4 w-4" /> I'll rent
                </button>
                <span className="text-sm text-muted-foreground sm:self-center">Still unsure? <a href={`tel:${COMPANY.phone}`} className="text-flame font-semibold hover:underline">Talk with our team</a></span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STEP 2 — CHOOSE YOUR MAT */}
      <section ref={productsRef} className="bg-ink relative overflow-hidden scroll-mt-[72px]" data-testid="gp-step2">
        <div className="absolute inset-0 grain opacity-10" />
        <div className="container-x py-20 md:py-24 relative">
          <div className="flex flex-col lg:flex-row lg:items-end gap-6 justify-between">
            <div className="max-w-2xl">
              <div className="kicker text-flame mb-3">Step 2 · Choose your mat</div>
              <h2 className="font-display font-black text-white text-3xl md:text-4xl tracking-tight">
                Start with the flagship. Browse every other mat below.
              </h2>
              <p className="mt-4 text-white/60 leading-relaxed">
                Big Bad Mats is where we start, not where we stop — timber, composite, steel, and rubber matting all follow,
                filtered by material, function, or load.
              </p>
            </div>
            <PathBadge path={path} onSwitch={() => step1Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
          </div>

          {/* Flagship pinned hero */}
          <FlagshipBigBadMats avenue={avenue} comparing={isComparing("big-bad-mats")} onCompareToggle={() => toggleCompare("big-bad-mats")} />

          {/* Filters */}
          <div className="mt-16 border-t border-white/10 pt-10">
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="axis-tabs">
              {AXES.map((a) => (
                <button key={a.key} data-testid={`axis-${a.key}`} onClick={() => changeAxis(a.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
                    axis === a.key ? "bg-flame text-white border-flame" : "bg-white/[0.04] text-white/80 border-white/15 hover:border-flame"
                  }`}>
                  <a.icon className="h-4 w-4" /> {a.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="value-chips">
              <Chip active={value === "all"} onClick={() => setValue("all")} testid="chip-all">
                All <span className="opacity-60">({counts.all})</span>
              </Chip>
              {activeAxis.values.map((v) => (
                <Chip key={v} active={value === v} onClick={() => setValue(v)} testid={`chip-${v.replace(/\W+/g, "-").toLowerCase()}`}>
                  {axis === "function" ? FUNCTION_LABEL[v] : v} <span className="opacity-60">({counts[v] || 0})</span>
                </Chip>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div className="mt-8">
            {loading ? (
              <div className="flex items-center justify-center py-24 text-white/50">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : visible.length === 0 ? (
              <p data-testid="empty-state" className="text-white/50 py-16 text-center">No mats in this category yet.</p>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="mat-grid">
                {visible.map((m, i) => (
                  <Reveal key={m.id} delay={(i % 3) * 0.05}>
                    <MatCard mat={m} avenue={avenue} open={expanded === m.id} onToggle={() => setExpanded(expanded === m.id ? null : m.id)}
                      comparing={isComparing(m.key)} onCompareToggle={() => toggleCompare(m.key)} />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* STEP 3 — GUIDED SELECTOR */}
      <section className="bg-charcoal text-white" data-testid="gp-step3">
        <div className="container-x py-20">
          <div className="border border-white/10 bg-white/[0.03] p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8 justify-between">
            <div className="max-w-2xl">
              <div className="kicker text-flame mb-3">Step 3 · Not sure which mat you need?</div>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">
                Answer 8 quick questions. Get a ranked recommendation.
              </h2>
              <p className="mt-4 text-white/60 leading-relaxed">
                Our Mat Selector weighs your loads, terrain, duration, budget, and site sensitivity — then recommends the
                1–3 best-fit mat types with a plain-language explanation of why. Your {path ? (path === "buy" ? "purchase" : "rental") : "purchase/rent"} choice carries straight into your quote.
              </p>
            </div>
            <div className="flex flex-col gap-3 shrink-0">
              <Link to={`/mat-selector?avenue=${avenue}`} data-testid="gp-selector-cta"
                className="inline-flex items-center justify-center gap-2 bg-flame text-white px-7 py-4 font-semibold hover:bg-flame/90 transition-colors">
                <Wand2 className="h-5 w-5" /> Launch the Mat Selector
              </Link>
              <a href={`tel:${COMPANY.phone}`} data-testid="gp-selector-call"
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-7 py-4 font-semibold hover:border-flame hover:text-flame transition-colors">
                <Phone className="h-4 w-4" /> Contact Us
              </a>
            </div>
          </div>

          {/* Learn more */}
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            <span className="text-white/40 uppercase tracking-widest text-[11px]">Learn more</span>
            <a href="/mats-presentation-flyer.pdf" target="_blank" rel="noopener noreferrer" data-testid="gp-presentation-flyer-link" className="inline-flex items-center gap-2 text-white/70 hover:text-flame transition-colors">
              <FileText className="h-4 w-4" /> Resource Preservation Flyer
            </a>
            <Link to="/ground-protection-flyer" data-testid="gp-flyer-link" className="inline-flex items-center gap-2 text-white/70 hover:text-flame transition-colors">
              <FileText className="h-4 w-4" /> One-page flyer
            </Link>
            <Link to="/ground-protection-deck" data-testid="gp-deck-link" className="inline-flex items-center gap-2 text-white/70 hover:text-flame transition-colors">
              <Presentation className="h-4 w-4" /> Purchase vs. Rent deck
            </Link>
          </div>
        </div>
      </section>

      {/* STEWARDSHIP */}
      <section className="bg-background border-t border-border" data-testid="gp-stewardship-callout">
        <div className="container-x py-16">
          <Reveal>
            <div className="border border-border bg-card p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
              <div className="max-w-2xl">
                <div className="kicker text-flame mb-3">More Than Products · A Preservation Solution</div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-ink tracking-tight">
                  Reusable matting protects the land as a long-cycle asset.
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  Big Bad Mats and our full matting fleet keep soil and topsoil intact — preserving value for future
                  agriculture and livestock, and giving operators visionary stewardship leadership.
                </p>
              </div>
              <Link to="/land-stewardship" data-testid="gp-stewardship-cta" className="inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-4 font-semibold hover:bg-flame/90 transition-colors whitespace-nowrap flex-shrink-0">
                See our stewardship approach <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection faqs={gpFaqs} />

      <CTABand />

      {/* Sticky compare bar */}
      {compareKeys.length > 0 && (
        <div data-testid="gp-compare-bar" className="fixed bottom-0 inset-x-0 z-40 bg-ink/95 backdrop-blur border-t border-flame/40 print:hidden">
          <div className="container-x py-3.5 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 text-white font-semibold text-sm">
              <Scale className="h-4 w-4 text-flame" /> Compare ({compareKeys.length})
            </span>
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
              {compareKeys.map((k) => {
                const m = k === "big-bad-mats" ? BBM_COMPARE : mats.find((mm) => mm.key === k);
                if (!m) return null;
                return (
                  <span key={k} data-testid={`compare-chip-${k}`} className="inline-flex items-center gap-1.5 text-xs text-white/85 border border-white/15 bg-white/[0.04] pl-2.5 pr-1.5 py-1">
                    {m.name}
                    <button onClick={() => toggleCompare(k)} className="text-white/50 hover:text-flame" aria-label={`Remove ${m.name}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
              {compareColumns.autoBBM && (
                <span className="inline-flex items-center gap-1.5 text-[11px] text-flame/90 border border-flame/40 bg-flame/10 px-2.5 py-1">
                  <Star className="h-3 w-3 fill-current" /> Big Bad Mats added (flagship)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid="gp-compare-clear" onClick={clearCompare}
                className="text-white/60 hover:text-white text-sm px-3 py-2">Clear</button>
              <button data-testid="gp-compare-open" onClick={openCompare} disabled={!canCompare}
                className="inline-flex items-center gap-2 bg-flame text-white px-5 py-2.5 text-sm font-semibold hover:bg-flame/90 disabled:opacity-40 transition-colors">
                {canCompare ? "Compare side by side" : "Select 2+ to compare"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <ComparisonDialog open={compareOpen} onOpenChange={setCompareOpen} columns={compareColumns.cols} autoBBM={compareColumns.autoBBM} avenue={avenue} />
    </div>
  );
}

function PathBadge({ path, onSwitch }) {
  if (!path) {
    return (
      <div data-testid="gp-path-badge" className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.03] px-4 py-2.5 text-sm text-white/60">
        <ArrowUpRight className="h-4 w-4 text-flame rotate-[-90deg]" /> Pick Purchase or Rent above to tailor your quote
      </div>
    );
  }
  return (
    <div data-testid="gp-path-badge" className="inline-flex items-center gap-3 border border-flame/40 bg-flame/10 px-4 py-2.5 text-sm">
      <span className="text-white/80">You chose:</span>
      <span className="font-display font-bold text-flame">{path === "buy" ? "Purchase" : "Rent"}</span>
      <button data-testid="gp-path-switch" onClick={onSwitch} className="text-white/50 hover:text-flame underline underline-offset-2">Switch</button>
    </div>
  );
}

function FlagshipBigBadMats({ avenue, comparing, onCompareToggle }) {
  const bbm = PRODUCTS.find((p) => p.slug === "big-bad-mats");
  if (!bbm) return null;
  const highlights = (bbm.highlights || []).slice(0, 4);
  return (
    <div className="mt-10 border border-flame/40 bg-white/[0.02]" data-testid="flagship-bigbadmats">
      <div className="grid gap-0 lg:grid-cols-12 items-stretch">
        <div className="lg:col-span-5 relative min-h-[280px]">
          <img src={bbm.image} alt={bbm.name} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-flame text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest">
            <Star className="h-3.5 w-3.5 fill-current" /> The Flagship
          </div>
        </div>
        <div className="lg:col-span-7 p-8 md:p-10">
          <div className="kicker text-flame mb-2">Start here · Big Bad Mats</div>
          <h3 className="font-display font-black text-white text-3xl md:text-4xl tracking-tight text-balance">
            Built to outlast the job. Reused for the next one.
          </h3>
          <p className="mt-4 text-white/70 leading-relaxed">
            Big Bad Mats is the flagship composite matting — engineered for maximum durability, traction, and reuse under
            the heaviest loads. When you want matting you can deploy again and again at the lowest cost-per-use, this is the
            workhorse we recommend.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {highlights.map((h) => (
              <div key={h} className="flex items-start gap-3 border border-white/10 bg-white/[0.03] p-3.5">
                <Check className="h-5 w-5 text-flame shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm leading-relaxed">{h}</span>
              </div>
            ))}
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={`/request-quote?avenue=${avenue}&source=mat-catalog&mat=${encodeURIComponent("Big Bad Mats")}`} data-testid="flagship-quote-cta"
              className="inline-flex items-center gap-2 bg-flame text-white px-7 py-3.5 font-semibold hover:bg-flame/90 transition-colors">
              {avenue === "rent" ? "Request a rental quote" : "Request a purchase quote"} <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/products/big-bad-mats" data-testid="flagship-explore-cta"
              className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 font-semibold hover:border-flame hover:text-flame transition-colors">
              Explore Big Bad Mats <ArrowRight className="h-4 w-4" />
            </Link>
            <button data-testid="flagship-compare-toggle" onClick={onCompareToggle}
              className={`inline-flex items-center gap-2 px-7 py-3.5 font-semibold border-2 transition-colors ${comparing ? "bg-flame border-flame text-white" : "border-flame text-flame hover:bg-flame hover:text-white"}`}>
              <Scale className="h-4 w-4" /> {comparing ? "Added to compare" : "Compare"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-3.5 py-1.5 text-sm border transition-colors ${
        active ? "bg-flame text-white border-flame" : "bg-white/[0.04] text-white/80 border-white/15 hover:border-flame"
      }`}>
      {children}
    </button>
  );
}

function MatCard({ mat, avenue, open, onToggle, comparing, onCompareToggle }) {
  return (
    <div data-testid={`mat-card-${mat.key}`} className={`group relative h-full flex flex-col border bg-white/[0.02] overflow-hidden hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] ${comparing ? "border-flame" : "border-white/10 hover:border-flame/60"}`}>
      <span aria-hidden="true" className="absolute top-0 left-0 z-20 h-full w-1 bg-flame origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out" />
      <div className="relative aspect-[4/3] overflow-hidden bg-ink">
        {mat.image ? (
          <img src={mat.image} alt={mat.name} className="h-full w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-110" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-white/30 text-sm">No image</div>
        )}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          <span className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide border backdrop-blur ${LOAD_STYLE[mat.load_rating] || "bg-white/10 text-white border-white/20"}`}>
            {mat.load_rating} Duty
          </span>
        </div>
        <button data-testid={`compare-toggle-${mat.key}`} onClick={onCompareToggle}
          className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide shadow-lg transition-colors ${comparing ? "bg-flame text-white" : "bg-white text-ink hover:bg-flame hover:text-white"}`}>
          {comparing ? <Check className="h-4 w-4" /> : <Scale className="h-4 w-4" />} {comparing ? "Added" : "Compare"}
        </button>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="text-[11px] uppercase tracking-[0.16em] text-flame font-semibold">{mat.material}</div>
        <h3 className="mt-1.5 font-display font-bold text-xl text-white">{mat.name}</h3>
        <p className="mt-2.5 text-sm text-white/55 leading-relaxed flex-1">{mat.description}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {(mat.functions || []).map((f) => (
            <span key={f} className="px-2 py-0.5 text-[11px] bg-white/[0.06] text-white/70 border border-white/10">{FUNCTION_LABEL[f] || f}</span>
          ))}
        </div>

        <button data-testid={`learn-more-${mat.key}`} onClick={onToggle}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-flame transition-colors self-start">
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {open ? "Show less" : "Learn more"}
        </button>

        {open && (
          <div data-testid={`mat-details-${mat.key}`} className="mt-4 space-y-3 border-t border-white/10 pt-4 text-sm">
            <Spec label="Dimensions" value={mat.dimensions} />
            <Spec label="Load capacity" value={mat.weight_capacity} />
            {(mat.applications || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-white/40 mb-1.5">Typical applications</div>
                <ul className="space-y-1">
                  {mat.applications.map((a) => (
                    <li key={a} className="flex items-start gap-2 text-white/70">
                      <span className="mt-1.5 h-1 w-1 bg-flame shrink-0" /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <button data-testid={`compare-toggle-footer-${mat.key}`} onClick={onCompareToggle}
          className={`mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-bold border-2 transition-colors ${comparing ? "bg-flame text-white border-flame" : "border-flame text-flame hover:bg-flame hover:text-white"}`}>
          {comparing ? <><Check className="h-4 w-4" /> Added to compare</> : <><Scale className="h-4 w-4" /> Compare this mat</>}
        </button>

        <div className="mt-3 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
          <span className="text-xs text-white/40">Pricing on request</span>
          <Link to={`/request-quote?avenue=${avenue}&source=mat-catalog&mat=${encodeURIComponent(mat.name)}`}
            data-testid={`request-pricing-${mat.key}`}
            className="inline-flex items-center gap-1.5 bg-white text-ink px-4 py-2.5 text-sm font-semibold hover:bg-flame hover:text-white transition-colors">
            {avenue === "rent" ? "Request rental" : "Request pricing"} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Spec({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-white/40">{label}</div>
      <div className="text-white/80">{value}</div>
    </div>
  );
}

const COMPARE_ROWS = [
  { label: "Material", render: (m) => <span className="text-white/85">{m.material}</span> },
  { label: "Load rating", render: (m) => (
    <span className={`inline-block px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide border ${LOAD_STYLE[m.load_rating] || "bg-white/10 text-white border-white/20"}`}>{m.load_rating} Duty</span>
  ) },
  { label: "Best for", render: (m) => (
    <div className="flex flex-wrap gap-1.5">
      {(m.functions || []).map((f) => (
        <span key={f} className="px-2 py-0.5 text-[11px] bg-white/[0.06] text-white/70 border border-white/10">{FUNCTION_LABEL[f] || f}</span>
      ))}
    </div>
  ) },
  { label: "Typical size", render: (m) => <span className="text-white/70 text-sm">{m.dimensions || "—"}</span> },
  { label: "Load capacity", render: (m) => <span className="text-white/70 text-sm leading-relaxed">{m.weight_capacity || "—"}</span> },
  { label: "Applications", render: (m) => (
    <ul className="space-y-1">
      {(m.applications || []).map((a) => (
        <li key={a} className="flex items-start gap-2 text-white/70 text-sm"><span className="mt-1.5 h-1 w-1 bg-flame shrink-0" /> {a}</li>
      ))}
    </ul>
  ) },
];

function ComparisonDialog({ open, onOpenChange, columns, autoBBM, avenue }) {
  const cols = columns || [];
  const gridTemplate = `minmax(120px, 150px) repeat(${cols.length}, minmax(220px, 1fr))`;
  const [showForm, setShowForm] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [ef, setEf] = useState({ name: "", email: "", company: "" });
  const [efErrors, setEfErrors] = useState({});

  useEffect(() => { if (open) { setShowForm(false); setSent(false); setEfErrors({}); } }, [open]);

  const submitEmail = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!ef.name.trim()) errs.name = "Enter your name.";
    if (!ef.email.trim()) errs.email = "Enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ef.email)) errs.email = "Enter a valid email.";
    setEfErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await api.post("/mat-comparison/email", {
        name: ef.name, email: ef.email, company: ef.company || "",
        avenue, site_url: window.location.origin,
        mats: cols.map((m) => ({
          name: m.name, material: m.material, load_rating: m.load_rating,
          functions: m.functions || [], dimensions: m.dimensions,
          weight_capacity: m.weight_capacity, applications: m.applications || [],
          flagship: !!m.flagship,
        })),
        visitor_id: getVisitorId(), attribution: getAttribution(),
      });
      setSent(true);
      trackEvent("calculator_complete", { email: ef.email, label: "Mat comparison emailed", product: "Ground Protection" });
      toast.success("Comparison sent — check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[96vw] max-h-[90vh] overflow-hidden p-0 bg-ink border-white/10 text-white" data-testid="gp-compare-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <DialogTitle className="font-display font-black text-2xl text-white flex items-center gap-2">
                <Scale className="h-5 w-5 text-flame" /> Mat comparison
              </DialogTitle>
              <DialogDescription className="text-sm text-white/60 mt-1">
                {autoBBM
                  ? <span className="flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-flame fill-current" /> Big Bad Mats is included as our flagship recommendation for these mat types.</span>
                  : "Compare specs side by side, then email it to yourself or request pricing."}
              </DialogDescription>
            </div>
            {!sent && (
              <button data-testid="compare-email-toggle" onClick={() => setShowForm((v) => !v)}
                className="shrink-0 inline-flex items-center justify-center gap-2 border-2 border-flame text-flame px-4 py-2.5 text-sm font-bold hover:bg-flame hover:text-white transition-colors">
                <Mail className="h-4 w-4" /> Email me this comparison
              </button>
            )}
          </div>

          {sent ? (
            <div data-testid="compare-email-sent" className="mt-3 flex items-center gap-2 text-emerald-400 text-sm">
              <Check className="h-4 w-4" /> Sent! Check your inbox — our team will follow up shortly.
            </div>
          ) : showForm && (
            <form onSubmit={submitEmail} data-testid="compare-email-form" className="mt-4 grid sm:grid-cols-4 gap-3 items-start" noValidate>
              <FieldDark label="Full name *" testid="compare-email-name" value={ef.name} error={efErrors.name} onChange={(e) => { setEf({ ...ef, name: e.target.value }); if (efErrors.name) setEfErrors({ ...efErrors, name: "" }); }} />
              <FieldDark label="Email *" testid="compare-email-email" type="email" value={ef.email} error={efErrors.email} onChange={(e) => { setEf({ ...ef, email: e.target.value }); if (efErrors.email) setEfErrors({ ...efErrors, email: "" }); }} />
              <FieldDark label="Company" testid="compare-email-company" value={ef.company} onChange={(e) => setEf({ ...ef, company: e.target.value })} />
              <button data-testid="compare-email-submit" disabled={sending}
                className="inline-flex items-center justify-center gap-2 bg-flame text-white px-5 py-2.5 text-sm font-semibold hover:bg-flame/90 disabled:opacity-50 transition-colors h-[42px] mt-[22px]">
                {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : <>Send to me</>}
              </button>
            </form>
          )}
        </DialogHeader>

        <div className="overflow-auto max-h-[calc(90vh-90px)]">
          <div className="min-w-max">
            {/* Header row */}
            <div className="grid sticky top-0 z-10 bg-ink" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="p-4 border-b border-r border-white/10" />
              {cols.map((m) => (
                <div key={m.key} data-testid={`compare-col-${m.key}`} className={`p-4 border-b border-white/10 ${m.flagship ? "bg-flame/10 border-l border-r border-flame/40" : "border-r border-white/10"}`}>
                  <div className="relative aspect-[4/3] overflow-hidden bg-charcoal mb-3">
                    {m.image && <img src={m.image} alt={m.name} className="h-full w-full object-cover" />}
                    {m.flagship && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-flame text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                        <Star className="h-3 w-3 fill-current" /> Flagship
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold text-white leading-tight">{m.name}</div>
                </div>
              ))}
            </div>
            {/* Spec rows */}
            {COMPARE_ROWS.map((row) => (
              <div key={row.label} className="grid" style={{ gridTemplateColumns: gridTemplate }}>
                <div className="p-4 border-b border-r border-white/10 text-[11px] uppercase tracking-wide text-white/40 font-semibold">{row.label}</div>
                {cols.map((m) => (
                  <div key={m.key} className={`p-4 border-b border-white/10 ${m.flagship ? "bg-flame/[0.06] border-l border-r border-flame/40" : "border-r border-white/10"}`}>
                    {row.render(m)}
                  </div>
                ))}
              </div>
            ))}
            {/* CTA row */}
            <div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="p-4 border-r border-white/10" />
              {cols.map((m) => (
                <div key={m.key} className={`p-4 ${m.flagship ? "bg-flame/[0.06] border-l border-r border-flame/40" : "border-r border-white/10"}`}>
                  <Link to={`/request-quote?avenue=${avenue}&source=mat-catalog&mat=${encodeURIComponent(m.name)}`}
                    data-testid={`compare-quote-${m.key}`}
                    className={`inline-flex items-center gap-1.5 w-full justify-center px-4 py-2.5 text-sm font-semibold transition-colors ${m.flagship ? "bg-flame text-white hover:bg-flame/90" : "bg-white text-ink hover:bg-flame hover:text-white"}`}>
                    {avenue === "rent" ? "Request rental" : "Request pricing"} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function FieldDark({ label, testid, type = "text", value, onChange, error }) {
  return (
    <div>
      <label className="block text-xs text-white/60 mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} aria-invalid={!!error}
        className={`w-full bg-white/5 border px-3.5 py-2.5 text-white placeholder:text-white/30 focus:outline-none h-[42px] ${error ? "border-red-500 focus:border-red-500" : "border-white/15 focus:border-flame"}`} />
      {error && <p data-testid={`${testid}-error`} className="mt-1 text-xs text-red-400 font-medium">{error}</p>}
    </div>
  );
}
